'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentViewer, GeneralNotFound, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventDocument } from 'src/api/personal/eventDetail';
import { getDocumentTypes } from 'src/api/admin/documentTypes';

export const Route = createFileRoute('/personal_/events/$eventId/documents/$documentId')({
	component: DocumentDetailPage,
});

function DocumentDetailPage() {
	const { eventId, documentId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const asOwner = !hasAllEventsView;

	const { data: document, isLoading: documentLoading } = useQuery({
		queryKey: ['personal-event-document', eventId, documentId, asOwner],
		queryFn: () => getEventDocument(eventId, documentId, asOwner),
	});

	const { data: documentTypesData, isLoading: typesLoading } = useQuery({
		queryKey: ['document-types', 'events'],
		queryFn: () => getDocumentTypes('events'),
	});

	const apiUrlBase = asOwner
		? `/api/personal-dashboard/own-events/${eventId}/documents`
		: `/api/personal-dashboard/events/${eventId}/documents`;

	const handleClose = () => {
		navigate({ to: '/personal/events/$eventId/documents', params: { eventId } });
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event-document', eventId, documentId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event-documents', eventId] });
	};

	const isLoading = authLoading || documentLoading || typesLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!document || !authenticatedUser) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/personal/events/${eventId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={documentTypesData?.data || []}
			apiUrlBase={apiUrlBase}
			onRevalidateDocument={handleUpdate}
			permissions={{
				metadata: true,
				fullView: true,
				sensitiveView: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
				edit: hasAllEventsView || asOwner,
				archive: hasAllEventsView || asOwner,
				review: false,
			}}
		/>
	);
}
